// src/pages/Customers/CustomerGroupPricing.jsx

import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import useCustomerPricing from '../../hooks/useCustomerPricing';

const styles = {
    container: { 
        fontFamily: '"Inter", sans-serif',
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '20px'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '30px'
    },
    title: {
        fontSize: '28px',
        fontWeight: '800',
        color: '#0f172a',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
    },
    box: {
        background: 'white',
        padding: '32px',
        borderRadius: '16px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        border: '1px solid #e2e8f0',
        marginBottom: '24px'
    },
    formGroup: {
        marginBottom: '20px'
    },
    label: {
        display: 'block',
        marginBottom: '8px',
        fontWeight: '600',
        color: '#374151'
    },
    input: {
        width: '100%',
        padding: '12px 16px',
        fontSize: '16px',
        border: '1px solid #d1d5db',
        borderRadius: '8px',
        boxSizing: 'border-box'
    },
    select: {
        width: '100%',
        padding: '12px 16px',
        fontSize: '16px',
        border: '1px solid #d1d5db',
        borderRadius: '8px',
        background: 'white',
        boxSizing: 'border-box'
    },
    button: {
        padding: '12px 24px',
        border: 'none',
        background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
        color: 'white',
        borderRadius: '8px',
        cursor: 'pointer',
        fontSize: '15px',
        fontWeight: '600',
        transition: 'all 0.2s',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.3)'
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        marginTop: '20px'
    },
    th: {
        background: '#f9fafb',
        padding: '16px',
        textAlign: 'left',
        borderBottom: '2px solid #e5e7eb',
        fontWeight: '600',
        color: '#374151'
    },
    td: {
        padding: '16px',
        borderBottom: '1px solid #e5e7eb',
        verticalAlign: 'middle'
    },
    deleteButton: {
        background: '#fee2e2',
        border: 'none',
        color: '#ef4444',
        padding: '8px 12px',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '13px',
        fontWeight: '600',
        transition: 'all 0.2s',
        display: 'flex',
        alignItems: 'center',
        gap: '6px'
    },
    grid2Col: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '20px',
        alignItems: 'end'
    }
};

function CustomerGroupPricing() {
    const { userData } = useAuth();
    const {
        customerGroupPrices, saveGroupPrice, deleteGroupPrice,
        customerGroups
    } = useCustomerPricing(userData?.companyId);
    
    const [productGroups, setProductGroups] = useState([]);
    const [loading, setLoading] = useState(true);

    // Form state
    const [selectedCustomerGroup, setSelectedCustomerGroup] = useState('');
    const [selectedProductGroup, setSelectedProductGroup] = useState('');
    const [discountType, setDiscountType] = useState('percentage');
    const [discountValue, setDiscountValue] = useState('');

    useEffect(() => {
        if (!userData?.companyId) return;

        const loadData = async () => {
            try {
                setLoading(true);
                // Load product groups
                const groupsRef = collection(db, 'companies', userData.companyId, 'productGroups');
                const groupsSnap = await getDocs(groupsRef);
                setProductGroups(groupsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            } catch (error) {
                console.error('Error loading data:', error);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [userData]);

    const handleAddGroupPrice = async () => {
        if (!selectedCustomerGroup || !selectedProductGroup || !discountValue) {
            alert('Lütfen müşteri grubu, ürün grubu ve indirim değeri seçin.');
            return;
        }

        const cGroup = customerGroups.find(g => g.id === selectedCustomerGroup);
        const pGroup = productGroups.find(g => g.id === selectedProductGroup);

        if (!cGroup || !pGroup) return;

        try {
            await saveGroupPrice(
                cGroup.id,
                cGroup.name,
                pGroup.id,
                pGroup.name,
                discountType,
                parseFloat(discountValue)
            );
            setSelectedCustomerGroup('');
            setSelectedProductGroup('');
            setDiscountValue('');
            setDiscountType('percentage');
        } catch (error) {
            console.error('Error saving group price:', error);
            alert('Grup fiyatı kaydedilirken bir hata oluştu.');
        }
    };

    const handleDeleteGroupPrice = async (groupPriceId) => {
        if (!window.confirm('Bu grup fiyatlandırmasını silmek istediğinizden emin misiniz?')) return;
        try {
            await deleteGroupPrice(groupPriceId);
        } catch (error) {
            console.error('Error deleting group price:', error);
            alert('Fiyat silinirken bir hata oluştu.');
        }
    };

    if (loading) {
        return <div style={styles.container}>Yükleniyor...</div>;
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div>
                    <h1 style={styles.title}><span role="img" aria-label="tags">🏷️</span> Grup Fiyatlandırmaları</h1>
                    <p style={{ color: '#64748b', marginTop: '8px', fontSize: '15px' }}>
                        Müşteri grupları ve ürün kategorilerine göre global indirim veya sabit fiyat kuralları tanımlayın.
                    </p>
                </div>
            </div>

            <div style={styles.box}>
                <h2 style={{ fontSize: '18px', marginBottom: '24px', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span role="img" aria-label="add">➕</span> Yeni Grup İndirimi Ekle
                </h2>
                
                <div style={{ padding: '20px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '24px' }}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Müşteri Grubu</label>
                        <select
                            style={styles.select}
                            value={selectedCustomerGroup}
                            onChange={(e) => setSelectedCustomerGroup(e.target.value)}
                        >
                            <option value="">Seçiniz...</option>
                            {customerGroups.map(group => (
                                <option key={group.id} value={group.id}>{group.name}</option>
                            ))}
                        </select>
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Ürün Kategorisi (Grubu)</label>
                        <select
                            style={styles.select}
                            value={selectedProductGroup}
                            onChange={(e) => setSelectedProductGroup(e.target.value)}
                        >
                            <option value="">Seçiniz...</option>
                            {productGroups.map(group => (
                                <option key={group.id} value={group.id}>{group.name}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div style={styles.grid2Col}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>İndirim Tipi</label>
                        <select
                            style={styles.select}
                            value={discountType}
                            onChange={(e) => setDiscountType(e.target.value)}
                        >
                            <option value="percentage">Yüzde (%) İndirimi</option>
                            <option value="fixed">Sabit Miktar İndirimi</option>
                        </select>
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Değer</label>
                        <input
                            type="number"
                            style={styles.input}
                            value={discountValue}
                            onChange={(e) => setDiscountValue(e.target.value)}
                            placeholder={discountType === 'percentage' ? 'Örn: 20' : 'Örn: 100'}
                        />
                    </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button style={styles.button} onClick={handleAddGroupPrice}>
                        <span role="img" aria-label="save">💾</span> Kuralı Kaydet
                    </button>
                </div>
            </div>

            <div style={styles.box}>
                <h2 style={{ fontSize: '18px', marginBottom: '24px', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span role="img" aria-label="list">📋</span> Tanımlı Fiyatlandırma Kuralları
                </h2>
                {customerGroupPrices.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '48px 20px', background: '#f8fafc', borderRadius: '12px', border: '1px dashed #cbd5e1' }}>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🏷️</div>
                        <h3 style={{ fontSize: '16px', color: '#334155', marginBottom: '8px' }}>Henüz kural tanımlanmamış</h3>
                        <p style={{ color: '#64748b', fontSize: '14px', maxWidth: '400px', margin: '0 auto' }}>
                            Yukarıdaki formu kullanarak ilk grup fiyatlandırma kuralınızı oluşturabilirsiniz. Müşterileriniz sipariş verdiğinde bu kurallar otomatik olarak uygulanacaktır.
                        </p>
                    </div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Müşteri Grubu</th>
                                    <th style={styles.th}>Ürün Kategorisi</th>
                                    <th style={styles.th}>İndirim Tipi</th>
                                    <th style={styles.th}>Değer</th>
                                    <th style={styles.th}>İşlem</th>
                                </tr>
                            </thead>
                            <tbody>
                                {customerGroupPrices.map(price => (
                                    <tr key={price.id}>
                                        <td style={styles.td}>
                                            <span style={{
                                                background: '#f1f5f9',
                                                padding: '4px 10px',
                                                borderRadius: '20px',
                                                fontSize: '13px',
                                                fontWeight: '600',
                                                color: '#475569'
                                            }}>
                                                {price.customerGroupName}
                                            </span>
                                        </td>
                                        <td style={styles.td}>{price.productGroupName}</td>
                                        <td style={styles.td}>
                                            <span style={{
                                                background: price.discountType === 'percentage' ? '#dcfce7' : '#dbeafe',
                                                color: price.discountType === 'percentage' ? '#166534' : '#1e40af',
                                                padding: '4px 10px',
                                                borderRadius: '12px',
                                                fontSize: '12px',
                                                fontWeight: '600'
                                            }}>
                                                {price.discountType === 'percentage' ? '% İndirim' : 'Sabit İndirim'}
                                            </span>
                                        </td>
                                        <td style={{ ...styles.td, fontWeight: '700', fontSize: '16px', color: price.discountType === 'percentage' ? '#10b981' : '#3b82f6' }}>
                                            {price.discountType === 'percentage' ? `%${price.discountValue}` : `${price.discountValue} ₺`}
                                        </td>
                                        <td style={styles.td}>
                                            <button
                                                style={styles.deleteButton}
                                                onClick={() => handleDeleteGroupPrice(price.id)}
                                                title="Bu kuralı sil"
                                            >
                                                <span role="img" aria-label="delete">🗑️</span> Sil
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

export default CustomerGroupPricing;
